import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PublicNav from "@/components/PublicNav";
import { LayoutDashboard, Users, Briefcase, FileText, BellRing, Search, Download } from "lucide-react";
import { PERIODS } from "./admin-ops/constants";
import { EmployersTab } from "./admin-ops/EmployersTab";
import { CandidatesTab } from "./admin-ops/CandidatesTab";
import { OffersTab } from "./admin-ops/OffersTab";
import { AlertsTab } from "./admin-ops/AlertsTab";
import { EmployerDetail } from "./admin-ops/EmployerDetail";
import { CandidateDetail } from "./admin-ops/CandidateDetail";
import { OfferDetail } from "./admin-ops/OfferDetail";

export default function AdminOps() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState("30d");
  const [dateFrom, setDateFrom] = useState("");
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [tab, setTab] = useState("employeurs");
  const [presetStatus, setPresetStatus] = useState("");
  const [detail, setDetail] = useState(null); // {kind, data}
  const [error, setError] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [cvTotal, setCvTotal] = useState(0);

  const allowed = user && user.role === "super_admin";

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
    else if (!authLoading && user && user.role !== "super_admin") navigate("/dashboard", { replace: true });
  }, [user, authLoading, navigate]);

  const loadStats = useCallback(async () => {
    const params = new URLSearchParams({ period });
    if (period === "custom" && dateFrom) params.set("date_from", dateFrom);
    const [s, a, c] = await Promise.all([
      api.get(`/admin-ops/stats?${params}`),
      api.get("/admin-ops/alerts"),
      api.get("/admin-ops/cvs?limit=1"),
    ]);
    setStats(s.data);
    setAlerts(a.data);
    setCvTotal(c.data.total || 0);
  }, [period, dateFrom]);

  useEffect(() => {
    if (allowed) loadStats().catch((e) => setError(formatApiError(e)));
  }, [allowed, loadStats]);

  useEffect(() => {
    if (!searchQ.trim()) { setSearchResults(null); return; }
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/admin-ops/search?q=${encodeURIComponent(searchQ)}`);
        setSearchResults(res.data);
      } catch (err) { setError(formatApiError(err)); }
    }, 350);
    return () => clearTimeout(t);
  }, [searchQ]);

  if (!user || !allowed) return null;

  const openCard = (target, status = "") => {
    setTab(target);
    setPresetStatus(status);
  };

  const openDetail = async (kind, id) => {
    setError("");
    try {
      const res = await api.get(`/admin-ops/${kind}/${id}`);
      setDetail({ kind, data: res.data, id });
    } catch (err) { setError(formatApiError(err)); }
  };

  const CARDS = stats ? [
    { key: "employeurs_total", label: "Employeurs enregistrés", tab: "employeurs" },
    { key: "employeurs_interesses", label: "Employeurs intéressés", tab: "employeurs", status: "interesse" },
    { key: "employeurs_a_contacter", label: "Nouveaux à contacter", tab: "employeurs", status: "a_contacter" },
    { key: "employeurs_contactes", label: "Employeurs contactés", tab: "employeurs", status: "premier_contact" },
    { key: "rendez_vous_planifies", label: "Rendez-vous planifiés", tab: "employeurs", status: "rendez_vous_planifie" },
    { key: "employeurs_confirmes", label: "Clients confirmés", tab: "employeurs", status: "client_confirme" },
    { key: "employeurs_documents_recus", label: "Documents reçus", tab: "employeurs", status: "documents_recus" },
    { key: "offres_actives", label: "Offres actives", tab: "offres", status: "active" },
    { key: "candidats_inscrits", label: "Candidats inscrits", tab: "candidats" },
    { key: "candidats_interesses", label: "Candidats intéressés", tab: "candidats" },
    { key: "candidatures_recues", label: "Candidatures reçues", tab: "offres" },
    { key: "candidats_preselectionnes", label: "Candidats présélectionnés", tab: "candidats", status: "preselectionne" },
    { key: "entretiens_planifies", label: "Entretiens planifiés", tab: "candidats", status: "entretien_planifie" },
    { key: "candidats_retenus", label: "Candidats retenus", tab: "candidats", status: "retenu" },
    { key: "placements_realises", label: "Placements réalisés", tab: "candidats", status: "recrutement_termine" },
    { key: "dossiers_en_attente", label: "Dossiers en attente", tab: "alertes" },
  ] : [];

  return (
    <div data-testid="admin-ops-page" className="min-h-screen bg-slate-50">
      <PublicNav />

      <div className="bg-brand-navy">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white flex items-center gap-3">
            <LayoutDashboard className="w-7 h-7 text-brand-turquoise" />
            Tableau de bord — Suivi commercial et recrutement
          </h1>
          <p className="text-white/70 text-sm mt-1">Pilotage en temps réel des employeurs, candidats et offres — données réelles, fiches cliquables.</p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <div data-testid="ops-period-selector" className="flex flex-wrap gap-1.5 bg-white/10 rounded-lg p-1 max-w-full">
              {PERIODS.map((p) => (
                <button key={p.key} data-testid={`ops-period-${p.key}`} onClick={() => setPeriod(p.key)}
                  className={`text-xs font-semibold rounded-md px-3 py-2 transition-colors ${
                    period === p.key ? "bg-brand-turquoise text-white" : "text-white/70 hover:text-white"}`}>
                  {p.label}
                </button>
              ))}
            </div>
            {period === "custom" && (
              <input data-testid="ops-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="text-xs rounded-lg px-3 py-2 bg-white/10 text-white border border-white/20" />
            )}
            <div className="relative ml-auto w-full sm:w-72">
              <Search className="w-4 h-4 text-white/50 absolute left-3 top-2.5" />
              <input data-testid="ops-global-search" value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Recherche globale (entreprise, candidat, offre, CNP…)"
                className="w-full text-sm bg-white/10 text-white placeholder-white/50 rounded-lg pl-9 pr-3 py-2 border border-white/20 focus:outline-none focus:ring-2 focus:ring-brand-turquoise" />
              {searchResults && (
                <div data-testid="ops-search-results" className="absolute right-0 top-11 z-40 w-[calc(100vw-3rem)] max-w-96 bg-white rounded-xl border border-slate-200 shadow-xl p-3 space-y-2 text-sm max-h-80 overflow-y-auto">
                  {["employers", "candidates", "offers"].map((k) => (
                    <div key={k}>
                      <p className="text-xs font-semibold text-slate-400 uppercase">{{ employers: "Employeurs", candidates: "Candidats", offers: "Offres" }[k]}</p>
                      {searchResults[k].length === 0 && <p className="text-xs text-slate-300">Aucun résultat</p>}
                      {searchResults[k].map((r) => (
                        <button key={r.id} data-testid={`ops-search-${k}-${r.id}`}
                          onClick={() => { setSearchResults(null); setSearchQ(""); openDetail(k === "offers" ? "offers" : k, r.id); }}
                          className="block w-full text-left text-brand-navy hover:text-brand-turquoise py-1 truncate">{r.label}</button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {error && <div data-testid="ops-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}

        {alerts.length > 0 && (
          <div data-testid="ops-alerts-banner" className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-amber-800 flex items-center gap-2 mb-2">
              <BellRing className="w-4 h-4" /> {alerts.length} alerte(s) nécessitant une intervention
            </p>
            <div className="space-y-1">
              {alerts.slice(0, 5).map((a, i) => (
                <button key={i} data-testid={`ops-alert-${i}`}
                  onClick={() => openDetail(a.entity_type === "offer" ? "offers" : a.entity_type === "candidate" ? "candidates" : "employers", a.entity_id)}
                  className="block w-full text-left text-xs text-amber-800 hover:text-amber-950 hover:bg-amber-100 rounded-lg px-2.5 py-1.5 transition-colors">
                  <span className={`font-bold mr-2 ${a.severity === "haute" ? "text-red-600" : "text-amber-600"}`}>
                    {a.severity === "haute" ? "Haute" : "Moyenne"}
                  </span>
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div data-testid="ops-stat-cards" className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3">
          {CARDS.map((c) => (
            <button key={c.key} data-testid={`ops-card-${c.key}`} onClick={() => openCard(c.tab, c.status || "")}
              className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-brand-turquoise hover:shadow-sm transition-all">
              <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide leading-tight">{c.label}</p>
              <p className="text-2xl font-bold text-brand-navy mt-1">{stats.cards[c.key]}</p>
            </button>
          ))}
        </div>

        <Link data-testid="ops-cvtheque-card" to="/admin/listes/cvs"
          className="flex items-center gap-4 bg-white rounded-xl border border-slate-200 p-5 hover:border-brand-turquoise hover:shadow-sm transition-all">
          <div className="w-11 h-11 rounded-lg bg-brand-navy/5 flex items-center justify-center">
            <FileText className="w-5 h-5 text-brand-blue" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-brand-navy">CVthèque — Téléchargement des CV</p>
            <p className="text-xs text-slate-500 truncate">{cvTotal} CV au registre — fichiers PDF/DOC des candidats, téléchargement direct et export CSV.</p>
          </div>
          <Download className="w-4 h-4 text-brand-turquoise shrink-0" />
        </Link>

        <div data-testid="ops-tabs" className="flex gap-1.5 bg-white border border-slate-200 rounded-lg p-1 max-w-full overflow-x-auto">
          {[["employeurs", "Employeurs", Users], ["candidats", "Candidats", FileText], ["offres", "Offres d'emploi", Briefcase], ["alertes", `Alertes (${alerts.length})`, BellRing]].map(([k, label, Icon]) => (
            <button key={k} data-testid={`ops-tab-${k}`} onClick={() => setTab(k)}
              className={`flex items-center gap-1.5 text-xs font-semibold rounded-md px-4 py-2 transition-colors ${
                tab === k ? "bg-brand-navy text-white" : "text-slate-500 hover:text-brand-navy"}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        {tab === "employeurs" && <EmployersTab presetStatus={presetStatus} onOpen={(id) => openDetail("employers", id)} />}
        {tab === "candidats" && <CandidatesTab presetStatus={presetStatus} onOpen={(id) => openDetail("candidates", id)} />}
        {tab === "offres" && <OffersTab presetStatus={presetStatus} onOpen={(id) => openDetail("offers", id)} />}
        {tab === "alertes" && <AlertsTab alerts={alerts} onOpen={(a) => openDetail(a.entity_type === "offer" ? "offers" : a.entity_type === "candidate" ? "candidates" : "employers", a.entity_id)} />}
      </div>

      {detail?.kind === "employers" && (
        <EmployerDetail id={detail.id} data={detail.data} onClose={() => setDetail(null)}
          onChanged={() => { openDetail("employers", detail.id); loadStats(); }}
          onOpenCandidate={(cid) => openDetail("candidates", cid)}
          onOpenOffer={(oid) => openDetail("offers", oid)} />
      )}
      {detail?.kind === "candidates" && (
        <CandidateDetail id={detail.id} data={detail.data} onClose={() => setDetail(null)}
          onChanged={() => { openDetail("candidates", detail.id); loadStats(); }}
          onOpenOffer={(oid) => openDetail("offers", oid)} />
      )}
      {detail?.kind === "offers" && (
        <OfferDetail id={detail.id} data={detail.data} onClose={() => setDetail(null)}
          onOpenCandidate={(cid) => openDetail("candidates", cid)} />
      )}
    </div>
  );
}
